import { useEffect, useState, FormEvent } from 'react';
import { aboutInfo } from '@/mocks/about';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  useEffect(() => {
    document.title = '关于我们｜漫境 — 成人漫画精选推荐';
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = fd.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      setFormData({ name: '', email: '', message: '' });
      return;
    }

    setFormStatus('loading');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9q9p77nahb7382ov8b0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: fd.get('name') as string,
          email: fd.get('email') as string,
          message: fd.get('message') as string,
        }).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setFormStatus('error');
        setFormMessage(serverMsg.includes('spam') ? '提交失败，请稍后再试' : serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试');
    }
  };

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebPage" data={{ name: '关于我们 - 漫境', url: 'https://manga-realm.readdy.ai/about' }} />
      <main className="min-h-screen pt-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          <section className="mb-16">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于漫境
            </h1>
            <p className="text-sm text-foreground-500">{aboutInfo.tagline}</p>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-4">我们的使命</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">{aboutInfo.mission}</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-4">品牌故事</h2>
                {aboutInfo.story.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="text-sm text-foreground-700 leading-relaxed mb-4 last:mb-0">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
              口碑声誉
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutInfo.reputation.map((item) => (
                <div key={item.label} className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                  <div className="font-heading text-2xl md:text-3xl font-bold text-accent-500">{item.value}</div>
                  <p className="text-sm text-foreground-500 mt-2">{item.label}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">联系方式</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-wechat-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">微信公众号</p>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">办公地址</p>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">工作时间</p>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.hours}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">留言咨询</h2>
              <form
                onSubmit={handleSubmit}
                className="space-y-4 bg-background-100 rounded-lg p-6 border border-background-200/70"
                data-readdy-form=""
              >
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                  aria-hidden="true"
                  className="bot-field"
                />
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                    rows={4}
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  />
                  <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                </div>
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-md hover:bg-primary-600 disabled:opacity-60 transition-all cursor-pointer"
                >
                  {formStatus === 'loading' ? '提交中...' : '发送留言'}
                </button>
                {formMessage && (
                  <p className={`text-sm text-center ${formStatus === 'success' ? 'text-accent-600' : 'text-primary-600'}`}>
                    {formMessage}
                  </p>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}